/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/hooks/useFeatureFlag.ts
 *
 * Rôle :
 *   Hook React qui interroge `/api/feature-flags.php` (endpoint public
 *   limité, whitelist par catégorie côté serveur) et permet à n'importe
 *   quel composant de consulter l'état d'un flag :
 *
 *     const { enabled, loading } = useFeatureFlag('feature.scanner.qr');
 *     if (!enabled) return <FeatureUnavailable />;
 *
 *   Comportement fail-safe : si l'API est HS, si le flag n'existe pas
 *   dans la réponse, OU si le flag est explicitement désactivé →
 *   `enabled` vaut `false`. Cette politique évite qu'un module sensible
 *   (push, scanner, recommandations IA…) reste actif par défaut quand
 *   le serveur ne répond plus.
 *
 *   Cache TanStack Query (staleTime 60 s + gcTime 5 min) pour absorber
 *   les pics de trafic le jour J. Le serveur ajoute aussi un Cache-Control
 *   HTTP 60 s + ETag → double rideau de cache.
 *
 * Réutilisable :
 *   100 %. Aucun couplage TNE — n'importe quelle app DRANE peut adopter
 *   le pattern. Adapter uniquement `FEATURE_FLAGS_ENDPOINT`.
 *
 * Voir : CDC §10.16, V3 SQL, api/feature-flags.php, FeatureFlagsPage.
 *
 * @author Mika
 * @since  2026-05-05
 */

import { useQuery } from '@tanstack/react-query';
import { apiRequest, ApiError } from '@/api/client';

/** Catégorie d'un flag — alignée sur V3 SQL. */
export type FeatureFlagCategory = 'feature' | 'section' | 'integration';

/** Forme d'un flag tel que renvoyé par /api/feature-flags.php (public). */
export interface FeatureFlagPublic {
  is_enabled: boolean;
  label: string;
  description: string | null;
  category: FeatureFlagCategory;
  disabled_message_key: string | null;
}

/** Réponse complète de l'endpoint public. */
export interface FeatureFlagsApiResponse {
  ok: boolean;
  flags: Record<string, FeatureFlagPublic>;
  count: number;
  updated_at: string | null;
}

/** Résultat exposé par le hook. */
export interface UseFeatureFlagResult {
  /** `true` si le flag est connu ET actif. `false` sinon (fail-safe). */
  enabled: boolean;
  /** `true` tant que la requête initiale n'est pas résolue. */
  loading: boolean;
  /** Métadonnées du flag (label, description, message si désactivé). */
  flag: FeatureFlagPublic | null;
  /** Erreur réseau / HTTP, si la requête a échoué (rare grâce au fail-safe). */
  error: Error | null;
}

/** Endpoint REST — relatif (cf. apiRequest) pour le déploiement FTP en sous-dossier. */
const FEATURE_FLAGS_ENDPOINT = 'feature-flags.php';

/** Clé TanStack Query partagée entre tous les appels — un seul fetch global. */
export const FEATURE_FLAGS_QUERY_KEY = ['public', 'feature-flags'] as const;

/**
 * Charge la table publique des feature flags. Utilisé en interne par
 * `useFeatureFlag` mais exposé séparément pour les composants qui ont
 * besoin de plusieurs flags à la fois (ex. `FeatureFlagsPage` admin
 * en mode preview).
 */
export function useFeatureFlags() {
  return useQuery<FeatureFlagsApiResponse, Error>({
    queryKey: FEATURE_FLAGS_QUERY_KEY,
    queryFn: async () => {
      try {
        const data = await apiRequest<FeatureFlagsApiResponse>(FEATURE_FLAGS_ENDPOINT);
        // Normalisation : si `flags` est null/undef (cas BDD HS),
        // on retourne un objet vide plutôt que de crasher.
        return {
          ok: data.ok ?? false,
          flags: data.flags ?? {},
          count: data.count ?? 0,
          updated_at: data.updated_at ?? null,
        };
      } catch (err) {
        // L'API a explicitement un fallback côté serveur (200 + flags vides
        // si BDD HS) — donc une vraie erreur ici signifie un souci réseau.
        // On laisse remonter pour que TanStack Query gère le retry.
        if (err instanceof ApiError) {
          throw err;
        }
        throw err instanceof Error ? err : new Error(String(err));
      }
    },
    staleTime: 60_000, // 60 s — cohérent avec Cache-Control côté serveur
    gcTime: 5 * 60_000,
    retry: 1, // 1 retry suffit ; au-delà on bascule sur le fail-safe enabled=false
    refetchOnWindowFocus: false,
  });
}

/**
 * Récupère l'état d'un feature flag.
 *
 * @param key  Clé de flag (ex. `'feature.scanner.qr'`).
 * @returns    `{ enabled, loading, flag, error }`.
 *
 * @example
 *   const { enabled, loading } = useFeatureFlag('section.medias');
 *   if (loading) return <Spinner />;
 *   if (!enabled) return <SectionDisabledNotice />;
 *   return <MediasGallery />;
 */
export function useFeatureFlag(key: string): UseFeatureFlagResult {
  const { data, isLoading, error } = useFeatureFlags();

  const flag = data?.flags?.[key] ?? null;
  // Fail-safe : si flag inconnu OU is_enabled !== true → false.
  const enabled = flag !== null && flag.is_enabled === true;

  return {
    enabled,
    loading: isLoading,
    flag,
    error: error ?? null,
  };
}

// ──────────────────────────────────────────────────────────────────────────
// Notes pour rétro-port NOLAS :
//  - NOLAS n'a aucun feature flag : couper une fonction = commenter du code
//    et redéployer. Ce hook + l'endpoint public permettent un kill-switch
//    instantané sans rebuild — gros gain UX/ops.
//  - On évite délibérément un Provider Context custom : TanStack Query
//    sert de cache global partagé entre tous les composants. Moins de
//    code, moins de prop drilling, et le pattern est déjà familier des
//    autres pages (PagesListPage, PushComposerPage…).
// ──────────────────────────────────────────────────────────────────────────
